import { useQuery } from '@tanstack/react-query';
import React from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from 'react-router-dom';

// lodash
import _ from 'lodash';

// components
import { hooks } from '@cowbell/ui';
import CustomerPrime100AccountProfileWithoutRenewalChanges from '../../workflows/accounts-renewals/prime100/policyholder/CustomerPrime100AccountProfileWithoutRenewalChanges';
import CustomerPrime100AccountProfileWithRenewalChanges from '../../workflows/accounts-renewals/prime100/policyholder/CustomerPrime100AccountProfileWithRenewalChanges';
import P100CustomerRenewalAccountInfoReadOnly from '../../workflows/accounts-renewals/prime100/policyholder/P100CustomerRenewalAccountInfoReadOnly';
import { OneIncPayPolicy } from '../policies/OneIncPayPolicy';
import { GenericApproveQuote } from './GenericApproveQuote';
import { RenewalSummary } from './RenewalSummary';
import { Stepper, Steps } from './Stepper';
// services, hooks, utils
import { getQuote } from '../../../inbox/QuotesService';
import { QuoteStepLoader } from '../quotes/QuoteStepLoader';

import { ProductTypes } from '../../../types';
import CustomerP250RenewalAttestation from '../quotes/CustomerP250RenewalAttestation';
import { formatQuote } from './customerRenewalsUtils';

const ROOT_PATH = '/customer/quotes/:quoteId/renewals/:product';
const DEFAULT_REDIRECT = '/customer/policies';

const CustomerRenewalWorkflow = () => {
  // @ts-expect-error TS(2339): Property 'product' does not exist on type '{}'.
  const { product, quoteId, step: urlStep } = useParams();
  const history = useHistory();
  const initial = React.useRef(true);

  const path = `/customer/quotes/${quoteId}/renewals/${product}`;

  let initialSteps: any = [];
  if (product === ProductTypes.p100) {
    initialSteps = ['approve', 'accountDetails', 'attest', 'summary', 'pay'];
  } else if (product === ProductTypes.p250) {
    initialSteps = ['approve', 'attest', 'summary', 'pay'];
  }

  const { step, goForward, jumpTo, isCompleted, completeStep } = hooks.useSteps(
    {
      startStep: urlStep,
      initialSteps,
    }
  );

  const { isLoading: quoteLoading, data: quote } = useQuery(
    ['renewal-quote', quoteId],
    () => {
      return getQuote(quoteId, product)
        .then((response) => {
          return {
            rawQuote: response.data,
            formattedQuote: formatQuote(response.data, product),
          };
        })
        .catch((err) => err);
    },
    { refetchOnWindowFocus: false }
  );

  React.useEffect(() => {
    if (initial.current) {
      initial.current = false;
    } else {
      history.push(`${path}/${step}`);
    }

    // eslint-disable-next-line
  }, [step]);

  React.useEffect(() => {
    jumpTo(urlStep);
    // eslint-disable-next-line
  }, [urlStep]);

  function handleVerifyAccountDetails({ renewalChangeInAnswers }: any) {
    window.localStorage.setItem(
      `AccountProfileFlow-${quote.rawQuote.id}`,
      renewalChangeInAnswers
    );
    goForward();
  }

  function handleRenewalAccountProfile() {
    const renewalChangeInAnswersEntry = window.localStorage.getItem(
      `AccountProfileFlow-${quote?.rawQuote?.id}`
    );

    const renewalChangeInAnswers =
      renewalChangeInAnswersEntry != null
        ? JSON.parse(renewalChangeInAnswersEntry)
        : null;

    if (renewalChangeInAnswers) {
      return (
        <CustomerPrime100AccountProfileWithRenewalChanges
          quote={quote}
          onMovingAhead={handleFollowingStep}
        />
      );
    }

    return (
      <CustomerPrime100AccountProfileWithoutRenewalChanges
        quote={quote}
        onMovingAhead={handleFollowingStep}
      />
    );
  }

  function handleFollowingStep() {
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    completeStep();
    goForward();
  }

  if (quoteLoading) {
    return <QuoteStepLoader />;
  }

  if (!quote) {
    return;
  }

  return (
    <>
      <Stepper>
        {_.get(quote, 'rawQuote.customerStatus') === 'AVAILABLE' && (
          <Steps
            id="approve"
            step={step}
            isCompleted={isCompleted}
            jumpTo={jumpTo}
            lock={!isCompleted('attest')}
          >
            Approve
          </Steps>
        )}
        {product === ProductTypes.p100 && (
          <Steps
            id="accountDetails"
            step={step}
            isCompleted={isCompleted}
            jumpTo={jumpTo}
            lock={false}
          >
            Account Details
          </Steps>
        )}
        <Steps
          id="attest"
          step={step}
          isCompleted={isCompleted}
          jumpTo={jumpTo}
          lock={!isCompleted('summary')}
        >
          Attest
        </Steps>
        <Steps
          id="summary"
          step={step}
          isCompleted={isCompleted}
          jumpTo={jumpTo}
          lock={!isCompleted('pay')}
        >
          Renewal Summary
        </Steps>
        <Steps id="pay" step={step} isCompleted={isCompleted} jumpTo={jumpTo}>
          E-Pay
        </Steps>
      </Stepper>

      <Switch>
        <Route
          path={`${ROOT_PATH}/approve`}
          exact
          render={() => (
            <GenericApproveQuote
              data={quote}
              onMovingAhead={handleFollowingStep}
            />
          )}
        />
        <Route
          path={`${ROOT_PATH}/accountDetails`}
          exact
          render={() => (
            <P100CustomerRenewalAccountInfoReadOnly
              handleVerifyAccountDetails={handleVerifyAccountDetails}
            />
          )}
        />
        <Route
          path={`${ROOT_PATH}/attest`}
          exact
          render={() => {
            if (product === ProductTypes.p100) {
              return handleRenewalAccountProfile();
            }

            if (product === ProductTypes.p250) {
              return (
                <CustomerP250RenewalAttestation
                  data={quote}
                  onMovingAhead={handleFollowingStep}
                />
              );
            }
          }}
        />
        <Route
          path={`${ROOT_PATH}/summary`}
          exact
          render={() => <RenewalSummary product={product} data={quote} />}
        />
        <Route
          path={`${ROOT_PATH}/pay`}
          exact
          render={() => <OneIncPayPolicy />}
        />

        <Redirect to={DEFAULT_REDIRECT} />
      </Switch>
    </>
  );
};

export default CustomerRenewalWorkflow;
